/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// RGBARRY-CUSTOMIZATION: Free shipping progress tracker for shipping100 promo.
// Threshold is $100. Shows partial bar + "You're only $X away" below threshold,
// full bar + "You have received free shipping" when any shipping promo is applied
// (covers both threshold-met promos and any blanket all-orders-free promo).

import type { ReactElement } from 'react';
import type { OrderSummaryBasket } from '@/components/order-summary';
import { formatCurrency } from '@/lib/currency';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { FREE_SHIPPING_THRESHOLD, isFreeShippingQualified } from './free-shipping';

interface FreeShippingProgressProps {
    basket: OrderSummaryBasket;
    className?: string;
}

export function FreeShippingProgress({ basket, className }: FreeShippingProgressProps): ReactElement {
    const { currency } = useSite();
    const { i18n } = useTranslation();

    const shippingItem = basket.shippingItems?.[0];
    const hasAppliedShippingPromo = (shippingItem?.priceAdjustments?.length ?? 0) > 0;
    const subtotal = basket.productSubTotal ?? 0;
    const rawProgress = subtotal / FREE_SHIPPING_THRESHOLD;
    const progress = Math.min(rawProgress, 1);
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);

    // Promo applied (address entered + promo fired) OR all-orders-free promo active
    const isEarned = hasAppliedShippingPromo;
    // Subtotal meets threshold but no address yet — promo will fire at checkout
    const isQualified = !isEarned && isFreeShippingQualified(basket);
    const showFull = isEarned || isQualified;

    return (
        <div className={cn('space-y-2', className)}>
            <p className="text-sm leading-5">
                {showFull ? (
                    <span className="font-medium text-primary">You have received free shipping!</span>
                ) : (
                    <>
                        {"You're only "}
                        <strong className="font-semibold text-foreground">
                            {formatCurrency(remaining, i18n.language, currency)}
                        </strong>
                        {' away from FREE Shipping!'}
                    </>
                )}
            </p>
            <div
                className="w-full h-2.5 bg-muted rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={Math.round(progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progress toward free shipping">
                <div
                    className="h-full bg-primary rounded-full transition-[width] duration-500"
                    style={{ width: `${progress * 100}%` }}
                />
            </div>
        </div>
    );
}
